import type { NodeViewRenderer } from "@tiptap/core";

/** A node view that renders the node as <tag class> and lets ProseMirror manage its content. */
export function blockView(tag: keyof HTMLElementTagNameMap, className: string): NodeViewRenderer {
  return () => {
    const dom = document.createElement(tag);
    dom.className = className;
    return { dom, contentDOM: dom };
  };
}

export const codeBlockView: NodeViewRenderer = () => {
  const dom = document.createElement("pre");
  dom.className = "kd-code";
  const code = document.createElement("code");
  dom.append(code);
  return { dom, contentDOM: code };
};

/** h1/h2 with data-number for CSS counters. H2 gets a gutter toggle for `number: false` (spec §4.1). */
export const headingView: NodeViewRenderer = ({ node: initial, editor, getPos }) => {
  let node = initial;
  const level = node.attrs.level === 1 ? 1 : 2;
  const dom = document.createElement(level === 1 ? "h1" : "h2");
  dom.className = `kd-h kd-h${level}`;
  const text = document.createElement("span");
  text.className = "kd-h__text";

  let toggle: HTMLButtonElement | null = null;
  if (editor.isEditable && level === 2) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "kd-h__toggle";
    button.contentEditable = "false";
    button.textContent = "#";
    button.setAttribute("aria-label", "Numbered heading");
    button.addEventListener("mousedown", (e) => e.preventDefault());
    button.addEventListener("click", () => {
      if (!editor.isEditable) return;
      const pos = getPos();
      if (typeof pos !== "number") return;
      editor.view.dispatch(
        editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, number: node.attrs.number === false }),
      );
    });
    dom.append(button);
    toggle = button;
  }
  dom.append(text);

  const paint = () => {
    const numbered = node.attrs.number !== false;
    dom.dataset.number = String(numbered);
    if (toggle) {
      toggle.setAttribute("aria-pressed", String(numbered));
      toggle.title = numbered ? "Numbered. Click to remove the number." : "Not numbered. Click to number it.";
    }
  };
  paint();

  return {
    dom,
    contentDOM: text,
    update(next) {
      if (next.type !== node.type || (next.attrs.level === 1 ? 1 : 2) !== level) return false;
      node = next;
      paint();
      return true;
    },
    stopEvent: (event) => toggle !== null && event.target instanceof Node && toggle.contains(event.target),
    ignoreMutation: (m) => m.type !== "selection" && !text.contains(m.target),
  };
};
